import { ReactNode } from 'react';
import { HorizontalContainer } from '@shared/designSystem/Containers';
import { Text } from '@shared/designSystem/Texts';
import { GridTable, GridTableCell, GridTableRow, TableCellAlignment, TableColumn } from '@shared/designSystem/Tables';
import designSystemStyles from '@shared/styles/designSystemStyles';
import styles from '@shared/styles/components/SelectableList.module.css';

interface SelectableListColumn<T> {
    id: string;
    label: ReactNode;
    width?: string;
    align?: TableCellAlignment;
    renderCell: (item: T) => ReactNode;
}

interface SelectableListProps<T> {
    items: T[];
    columns: SelectableListColumn<T>[];
    onSelect: (item: T) => void;
    isSelected?: (item: T) => boolean;
    getItemKey: (item: T) => string;
    emptyStateText?: string;
    minWidth?: string;
}

export const SelectableList = <T,>({
    items,
    columns,
    onSelect,
    isSelected = () => false,
    getItemKey,
    emptyStateText = 'No hay datos para mostrar.',
    minWidth = '100%'
}: SelectableListProps<T>) => {
    const tableColumns: TableColumn[] = columns.map((column) => ({
        id: column.id,
        label: column.label,
        width: column.width,
        align: column.align
    }));

    const rowTemplate = columns.map((column) => column.width ?? '1fr').join(' ');

    return (
        <GridTable columns={tableColumns} rowTemplate={rowTemplate} minWidth={minWidth}>
            {items.length === 0 ? (
                <HorizontalContainer
                    width="100%"
                    justifyContent="center"
                    padding={designSystemStyles.paddingSm}
                    backgroundColor={designSystemStyles.colorBackgroundWhite}
                >
                    <Text text={emptyStateText} />
                </HorizontalContainer>
            ) : (
                items.map((item) => {
                    const selected = isSelected(item);
                    return (
                        <div
                            key={getItemKey(item)}
                            onClick={() => onSelect(item)}
                            role="button"
                            tabIndex={0}
                            aria-pressed={selected}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onSelect(item);
                                }
                            }}
                            className={styles['selectable-row-trigger']}
                        >
                            <GridTableRow className={`${styles['selectable-row']} ${selected ? styles['selected'] : ''}`}>
                                {columns.map((column) => (
                                    <GridTableCell key={column.id} align={column.align}>
                                        {column.renderCell(item)}
                                    </GridTableCell>
                                ))}
                            </GridTableRow>
                        </div>
                    );
                })
            )}
        </GridTable>
    );
};

